/**
 * FinancialPulse — Income/Payroll/Margin per session.
 *
 * 3 running-avg tiles + grouped-bar chart with trendlines. Read-only
 * mirror of the Payroll KPI Dashboard, so numbers match byte-for-byte.
 */
import React, { useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  ResponsiveContainer, ComposedChart, Bar, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from 'recharts';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  gold: '#B08A3A', border: '#E8E5EC',
  plum: '#4A3B5F', rose: '#8A3A3A', ok: '#3B6F5D',
};

const PERIOD_OPTIONS = [
  { key: '3',   label: '3-month avg',  window: 3 },
  { key: '6',   label: '6-month avg',  window: 6 },
  { key: '9',   label: '9-month avg',  window: 9 },
  { key: '12',  label: '12-month avg', window: 12 },
  { key: 'yoy', label: 'YoY (same period last year)', window: null },
];

function trendline(values) {
  const n = values.length;
  if (!n) return [];
  const xs = values.map((_, i) => i);
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = values.reduce((a, b) => a + b, 0) / n;
  const num = xs.reduce((s, x, i) => s + (x - meanX) * (values[i] - meanY), 0);
  const den = xs.reduce((s, x) => s + (x - meanX) ** 2, 0) || 1;
  const slope = num / den;
  const intercept = meanY - slope * meanX;
  return xs.map(x => intercept + slope * x);
}

function fmt$(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return `$${Number(v).toFixed(2)}`;
}

// Custom tooltip — only show the 3 bar series, hide the 3 trendline series.
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload) return null;
  const wanted = ['income_per_session', 'payroll_per_session', 'margin_per_session'];
  const shown = payload.filter(p => wanted.includes(p.dataKey));
  return (
    <div className="bg-white border rounded shadow-sm p-3 text-[12px]" style={{ borderColor: C.border }}>
      <div className="font-semibold mb-1" style={{ color: C.ink }}>{label}</div>
      {shown.map(p => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="font-semibold" style={{ color: C.ink }}>{fmt$(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

function AvgTile({ label, tone, values, mode, windowSize, testId }) {
  const { current, prior, delta, dataPoints } = useMemo(() => {
    if (!values.length) return { current: null, prior: null, delta: null, dataPoints: 0 };
    if (mode === 'yoy') {
      // YoY needs at least 13 data points to compare current vs same window
      // ending 12 months earlier. When we don't have enough, fall back to
      // "avg of everything we have" so the tile is never blank.
      if (values.length >= 13) {
        const N = Math.min(6, Math.floor((values.length - 12) / 2) || 1);
        const cur = values.slice(-N);
        const pri = values.slice(-12 - N, -12);
        const avg = a => a.length ? a.reduce((s, x) => s + x, 0) / a.length : null;
        const c = avg(cur), p = avg(pri);
        return { current: c, prior: p,
                 delta: p == null || !p ? null : ((c - p) / p) * 100,
                 dataPoints: values.length };
      }
      // Fallback while data is thin — average what we have.
      const avg = values.reduce((s, x) => s + x, 0) / values.length;
      return { current: avg, prior: null, delta: null, dataPoints: values.length };
    }
    // Running avg — use the last `windowSize` months when available,
    // otherwise average whatever we've got (user directive 2026-02-15).
    const w = Math.min(windowSize, values.length);
    const cur = values.slice(-w);
    const avg = a => a.length ? a.reduce((s, x) => s + x, 0) / a.length : null;
    const c = avg(cur);
    // Prior window (same size) — only when we have enough to compare.
    const pri = values.slice(-2 * w, -w);
    const p = pri.length === w ? avg(pri) : null;
    return { current: c, prior: p,
             delta: p == null || !p ? null : ((c - p) / p) * 100,
             dataPoints: w };
  }, [values, mode, windowSize]);

  return (
    <Card className="p-4 border text-center" style={{ borderColor: C.border }} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: tone }}>{label}</div>
      <div className="mt-1 text-2xl font-serif" style={{ color: C.ink }}>{fmt$(current)}</div>
      <div className="text-[11px]" style={{ color: C.soft }}>
        {current == null
          ? 'No finalized runs yet'
          : delta == null
            ? `Avg of ${dataPoints} run${dataPoints === 1 ? '' : 's'}${mode !== 'yoy' && dataPoints < windowSize ? ' · gathering data' : ''}`
            : (<>Avg of {dataPoints} runs · vs prior: <span style={{ color: delta >= 0 ? C.ok : C.rose, fontWeight: 600 }}>{delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%</span></>)
        }
      </div>
    </Card>
  );
}

export default function FinancialPulse({ data }) {
  const [period, setPeriod] = useState('6');
  const opt = PERIOD_OPTIONS.find(o => o.key === period) || PERIOD_OPTIONS[1];

  const chart = useMemo(() => {
    const base = (data || []).map(h => ({
      period: (h.period_end || '').slice(5),
      income_per_session:  (h.income_per_unit_cents  || h.income_per_show_unit_cents  || 0) / 100,
      payroll_per_session: (h.payroll_per_unit_cents || h.payroll_per_show_unit_cents || 0) / 100,
      margin_per_session:  (h.margin_per_unit_cents  || h.margin_per_show_unit_cents  || 0) / 100,
    }));
    const keys = ['income_per_session', 'payroll_per_session', 'margin_per_session'];
    const trends = Object.fromEntries(keys.map(k => [k, trendline(base.map(b => b[k]))]));
    return base.map((row, i) => ({
      ...row,
      income_per_session_trend:  trends.income_per_session[i],
      payroll_per_session_trend: trends.payroll_per_session[i],
      margin_per_session_trend:  trends.margin_per_session[i],
    }));
  }, [data]);

  const seriesValues = useMemo(() => ({
    income:  chart.map(r => r.income_per_session),
    payroll: chart.map(r => r.payroll_per_session),
    margin:  chart.map(r => r.margin_per_session),
  }), [chart]);

  if (!chart.length) {
    return (
      <Card className="p-6 border text-center text-sm" style={{ borderColor: C.border, color: C.soft }} data-testid="hr-financial-pulse-empty">
        No finalized payroll periods yet — Financial Pulse will populate once HR finalizes the first run.
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="hr-financial-pulse-wrap">
      {/* 3 running-avg tiles + period selector */}
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.gold }}>
          Financial Pulse · {opt.label}
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-52 h-8" data-testid="hr-financial-pulse-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map(o => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <AvgTile testId="hr-fp-tile-income"  label="Income / Session avg"  tone={C.plum}
                 values={seriesValues.income}  mode={period === 'yoy' ? 'yoy' : 'running'} windowSize={opt.window} />
        <AvgTile testId="hr-fp-tile-payroll" label="Payroll / Session avg" tone={C.rose}
                 values={seriesValues.payroll} mode={period === 'yoy' ? 'yoy' : 'running'} windowSize={opt.window} />
        <AvgTile testId="hr-fp-tile-margin"  label="Margin / Session avg"  tone={C.ok}
                 values={seriesValues.margin}  mode={period === 'yoy' ? 'yoy' : 'running'} windowSize={opt.window} />
      </div>

      <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="hr-financial-pulse">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.gold }}>
            Income / Payroll / Margin per session · monthly
          </div>
          <div className="text-[11px]" style={{ color: C.soft }}>
            Denominator = Show + LC + NS · from finalized Payroll runs
          </div>
        </div>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
              <XAxis dataKey="period" fontSize={10} tickLine={false} />
              <YAxis fontSize={10} tickLine={false} tickFormatter={v => `$${v.toFixed(0)}`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="income_per_session"  name="Income / Session"  fill={C.plum} radius={[4,4,0,0]} maxBarSize={22} />
              <Bar dataKey="payroll_per_session" name="Payroll / Session" fill={C.rose} radius={[4,4,0,0]} maxBarSize={22} />
              <Bar dataKey="margin_per_session"  name="Margin / Session"  fill={C.ok}   radius={[4,4,0,0]} maxBarSize={22} />
              <Line type="linear" dataKey="income_per_session_trend"  stroke={C.plum} strokeDasharray="4 3" strokeWidth={1.5} dot={false} legendType="none" tooltipType="none" />
              <Line type="linear" dataKey="payroll_per_session_trend" stroke={C.rose} strokeDasharray="4 3" strokeWidth={1.5} dot={false} legendType="none" tooltipType="none" />
              <Line type="linear" dataKey="margin_per_session_trend"  stroke={C.ok}   strokeDasharray="4 3" strokeWidth={2}   dot={false} legendType="none" tooltipType="none" />
              <ReferenceLine y={0} stroke="#000" strokeOpacity={0.15} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
